import { z } from 'zod';
/**
 * Closed sets carried verbatim from the PRDs. The verification states and the depth
 * gradient carry the meanings fixed in the architecture document and are not redefined
 * per feature (Garnet PRD §0.1).
 */
// ── Jurisdiction and region ────────────────────────────────────────────────────────────
export const homeRegion = z.enum(['eu', 'in', 'ae', 'uk']);
/** ISO 3166-1 alpha-2, plus 'eu' for union-level instruments. */
export const jurisdiction = z.string().regex(/^(eu|[a-z]{2})$/);
// ── Verification core (PRD §1.3, §6.3) ─────────────────────────────────────────────────
export const checkState = z.enum([
    'pending',
    'verified_machine',
    'verified_attestation',
    'unverified',
    'flag',
    'error',
]);
/** Orders state softness for weakest-link selection: flag < unverified < attestation < machine. */
export const CHECK_STATE_RANK = {
    flag: 0,
    unverified: 1,
    verified_attestation: 2,
    verified_machine: 3,
    pending: -1,
    error: -1,
};
export const depth = z.enum([
    'type_lawful',
    'identity_authentic',
    'unit_authentic',
    'provenance_clean',
]);
export const DEPTH_ORDER = {
    type_lawful: 0,
    identity_authentic: 1,
    unit_authentic: 2,
    provenance_clean: 3,
};
export const checkNumber = z.enum([
    'c1_identity',
    'c2_binding',
    'c3_classification',
    'c4_certificate',
    'c5_safety',
    'c6_unit',
    'c7_provenance',
    'e1_entity',
    'e2_ubo',
    'e3_standing',
    'e4_tax',
    'd1_cert_exists',
    'd2_designation',
    'd3_scope',
    't1_customs',
    'x1_sanctions',
    'x2_export_control',
    'i1_insurance',
]);
export const checkLayer = z.enum(['entity', 'asset', 'document']);
/** Six business-facing diligence categories (PRD §34). Every check maps to exactly one. */
export const diligenceCategory = z.enum([
    'corporate_ownership',
    'tax_customs',
    'sector_certifications',
    'sanctions_export_control',
    'insurance',
    'asset_unit_authenticity',
]);
export const CHECK_CATEGORY = {
    e1_entity: 'corporate_ownership',
    e2_ubo: 'corporate_ownership',
    e3_standing: 'corporate_ownership',
    e4_tax: 'tax_customs',
    t1_customs: 'tax_customs',
    c4_certificate: 'sector_certifications',
    d1_cert_exists: 'sector_certifications',
    d2_designation: 'sector_certifications',
    d3_scope: 'sector_certifications',
    x1_sanctions: 'sanctions_export_control',
    x2_export_control: 'sanctions_export_control',
    i1_insurance: 'insurance',
    c1_identity: 'asset_unit_authenticity',
    c2_binding: 'asset_unit_authenticity',
    c3_classification: 'asset_unit_authenticity',
    c5_safety: 'asset_unit_authenticity',
    c6_unit: 'asset_unit_authenticity',
    c7_provenance: 'asset_unit_authenticity',
};
export const CHECK_LAYER = {
    e1_entity: 'entity',
    e2_ubo: 'entity',
    e3_standing: 'entity',
    e4_tax: 'entity',
    t1_customs: 'entity',
    x1_sanctions: 'entity',
    x2_export_control: 'entity',
    i1_insurance: 'entity',
    c1_identity: 'asset',
    c2_binding: 'asset',
    c3_classification: 'asset',
    c5_safety: 'asset',
    c6_unit: 'asset',
    c7_provenance: 'asset',
    c4_certificate: 'document',
    d1_cert_exists: 'document',
    d2_designation: 'document',
    d3_scope: 'document',
};
// ── Flags (PRD Appendix B) ─────────────────────────────────────────────────────────────
export const flagType = z.enum([
    'misattribution',
    'down_classification',
    'body_withdrawn',
    'certificate_expired',
    'recall',
    'counterfeit_signal',
    'diversion_signal',
    'provenance_defect',
    'insolvency',
    'source_conflict',
    'ambiguous_identity',
    'sanctions_hit',
    'export_control_unlicensed',
    'insurance_lapsed',
    'other',
]);
export const severity = z.enum(['low', 'medium', 'high', 'blocking']);
/** Default severity per flag type (Appendix B: each carries a default severity). */
export const FLAG_DEFAULT_SEVERITY = {
    misattribution: 'high',
    down_classification: 'high',
    body_withdrawn: 'blocking',
    certificate_expired: 'high',
    recall: 'blocking',
    counterfeit_signal: 'blocking',
    diversion_signal: 'high',
    provenance_defect: 'high',
    insolvency: 'high',
    source_conflict: 'medium',
    ambiguous_identity: 'medium',
    sanctions_hit: 'blocking',
    export_control_unlicensed: 'blocking',
    insurance_lapsed: 'medium',
    other: 'low',
};
// ── Record, job, case (PRD Appendix D) ─────────────────────────────────────────────────
export const recordState = z.enum(['clear', 'clear_with_flags', 'blocked', 'expired']);
export const jobStatus = z.enum([
    'planned',
    'running',
    'awaiting_attestation',
    'composing',
    'done',
    'error',
]);
export const caseStatus = z.enum([
    'open',
    'investigating',
    'awaiting_counterparty',
    'resolved',
    'dismissed',
]);
export const caseTrigger = z.enum(['flag', 'dispute', 'manual', 'source_change']);
export const shareTier = z.enum(['public', 'restricted', 'authority']);
// ── Asset and claim (PRD §5.3) ─────────────────────────────────────────────────────────
export const assetClass = z.enum([
    'medical',
    'ivd',
    'electronics',
    'semiconductor',
    'automotive',
    'aeronautics',
]);
export const identifierType = z.enum([
    'basic_udi_di',
    'udi_di',
    'gtin',
    'reg_number',
    'part_number',
    'vin',
]);
export const counterpartyRole = z.enum([
    'manufacturer',
    'importer',
    'distributor',
    'authorised_rep',
    'refurbisher',
    'broker',
    'end_user',
]);
export const counterpartyStatus = z.enum([
    'draft',
    'verifying',
    'verified',
    'flagged',
    'rejected',
]);
export const sourceChannel = z.enum(['console', 'portal', 'gateway', 'import']);
// ── Entity layer (PRD §5.2) ────────────────────────────────────────────────────────────
export const corporateRegister = z.enum([
    'companies_house',
    'cro',
    'mca21',
    'bris_node',
    'ded',
    'freezone',
    'ede_holder',
    'other',
]);
export const entityStatus = z.enum([
    'active',
    'dissolved',
    'struck_off',
    'in_insolvency',
    'unknown',
]);
export const uboAccess = z.enum(['public', 'restricted', 'attestation', 'unavailable']);
// ── Evidence and sources (PRD §5.4, §7) ────────────────────────────────────────────────
export const evidenceKind = z.enum([
    'api_response',
    'portal_capture',
    'document',
    'photo',
    'scan',
    'attestation',
    'ledger_hit',
]);
export const sourceProtocol = z.enum(['rest', 'soap_dtx', 'dataset', 'portal_rpa', 'file_feed']);
export const sourceAuth = z.enum(['none', 'api_key', 'oauth', 'mutual_tls', 'actor_credential']);
export const sourceStatus = z.enum(['live', 'degraded', 'down', 'planned']);
export const sourceCapability = z.enum([
    'resolve',
    'verify',
    'recall',
    'certificate',
    'list',
    'health',
]);
export const connectionState = z.enum(['disconnected', 'connecting', 'connected', 'error']);
// ── Certificates, inspectors, attestation (PRD §5.5, §10) ─────────────────────────────
export const certificateScheme = z.enum([
    'ce_mdr',
    'ukca',
    'gmp',
    'e_mark',
    'ul',
    'fcc',
    'other',
]);
export const bodyDesignation = z.enum(['current', 'suspended', 'withdrawn', 'unknown']);
export const certificateStatus = z.enum(['valid', 'expired', 'suspended', 'withdrawn']);
export const inspectorKind = z.enum(['field_inspector', 'test_lab']);
export const inspectorStanding = z.enum(['active', 'probation', 'suspended']);
export const attestationMethod = z.enum([
    'physical_inspection',
    'as6171_test',
    'manufacturer_callback',
    'document_forensics',
]);
export const attestationOutcome = z.enum(['confirmed', 'refuted', 'inconclusive']);
// ── Documents and renewals (PRD §28) ───────────────────────────────────────────────────
export const documentType = z.enum([
    'ce_mdr_cert',
    'ukca_cert',
    'gmp_cert',
    'iso_13485',
    'md15_import_licence',
    'md_manufacture_licence',
    'ede_registration',
    'dha_doh_registration',
    'e_mark_approval',
    'fcc_grant',
    'ul_cert',
    'business_registration',
    'free_sale_certificate',
    'declaration_of_conformity',
    'power_of_attorney',
    'translation',
    'notarisation',
    'apostille',
    'legal_opinion',
    'insurance_certificate',
    'other',
]);
export const documentStatus = z.enum([
    'valid',
    'expiring',
    'expired',
    'suspended',
    'withdrawn',
    'renewing',
    'replaced',
]);
// ── Professional services (PRD §29) ────────────────────────────────────────────────────
export const professionalType = z.enum(['notary', 'trust_lawyer', 'translator', 'industry_assessor']);
export const professionalRequestStatus = z.enum([
    'open',
    'assigned',
    'in_progress',
    'awaiting_info',
    'needs_translation',
    'delivered',
    'refused',
    'disputed',
    'cancelled',
]);
// ── TPM (PRD §31) ──────────────────────────────────────────────────────────────────────
export const tpmTier = z.enum(['critical', 'high', 'standard', 'low']);
export const criticality = z.enum(['sole_source', 'single_source', 'multi_source']);
export const tierSetBy = z.enum(['rule', 'operator', 'crm']);
export const monitoringState = z.enum(['on_track', 'due_soon', 'overdue']);
// ── Requirements loop (PRD §36) ────────────────────────────────────────────────────────
export const requirementOrigin = z.enum(['suggested_from_finding', 'manual', 'advisor']);
export const requirementStatus = z.enum([
    'requested',
    'sent',
    'awaiting_counterparty',
    'received_under_review',
    'verified_resolved',
    'rejected_needs_revision',
]);
// ── Advisor (PRD §33.6) ────────────────────────────────────────────────────────────────
export const recommendationContext = z.enum([
    'intake',
    'document_review',
    'classification',
    'verification',
    'anomaly',
    'case',
    'drafting',
    'sourcing',
    'monitoring',
]);
export const recommendationKind = z.enum([
    'request_documents',
    'adjust_tier',
    'next_action',
    'flag_anomaly',
    'resolve_case',
    'draft',
    'request_before_award',
    'reverify',
]);
export const humanResponse = z.enum(['pending', 'accepted', 'modified', 'rejected']);
export const confidence = z.enum(['low', 'medium', 'high']);
// ── Decisions (PRD §35.2) ──────────────────────────────────────────────────────────────
export const decision = z.enum(['qualify', 'qualify_with_conditions', 'block']);
// ── Integrations (PRD §30, §32, §37) ───────────────────────────────────────────────────
export const crmSystem = z.enum(['salesforce', 'hubspot', 'dynamics365', 'zoho', 'pipedrive', 'generic']);
export const sourcingPlatform = z.enum(['ariba', 'coupa', 'jaggaer', 'gep_smart', 'ivalua', 'generic']);
export const grcSystem = z.enum([
    'servicenow_irm',
    'archer',
    'metricstream',
    'logicgate',
    'auditboard',
    'generic',
]);
export const rfpEventType = z.enum(['rfp', 'rfq', 'rfi']);
export const syncState = z.enum(['pending', 'synced', 'conflict', 'error']);
// ── IAM (PRD §16) ──────────────────────────────────────────────────────────────────────
export const orgStatus = z.enum(['active', 'suspended', 'closed']);
export const roleName = z.enum([
    'operator',
    'reviewer',
    'compliance_approver',
    'org_admin',
    'read_only',
    'inspector',
    'platform_admin',
]);
/** Principal types are first-class (CLAUDE.md §2.7) — what makes Invariant I3 structural. */
export const principalType = z.enum(['user', 'api_key', 'service']);
// ── Data classification (PRD §17.1) ────────────────────────────────────────────────────
export const dataClass = z.enum(['public', 'business', 'sensitive', 'secret']);
//# sourceMappingURL=enums.js.map